import React, { ReactElement, useCallback, useEffect, Fragment, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { useRouteMatch } from 'react-router-dom';

import {
    useTable,
    usePagination,
    useSortBy,
    useFilters,
    useAsyncDebounce,
    Column as ColumnType,
    TableOptions,
    UseSortByColumnOptions,
    UseFiltersColumnOptions,
} from 'react-table';
import { Paginated, PaginatedBaseMeta } from '../../services/api-handlers/pagination';
import Pagination from './Pagination';
import { QueryParams } from '../../types/grid';
import styles from './assets/grid.module.scss';
import textFilter from './filters/text-filter';
import useMemoCompare from './hooks/memo-compare';
import Loader from '../Loader';

import Dotdotdot from 'react-dotdotdot';
import { DateTimePicker, DropdownList } from 'react-widgets';
import CaseEnum from '../../enumerables/case-enum';
import OrderEnum from '../../enumerables/order-enum';
import GeneralEnum from '../../enumerables/general-enum';
import { getValFromEnum } from '../redux-form/utils/dropdown-values';
import user from '../../services/user-auth';
import UserRole from '../../enumerables/user-role';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleLeft, faAngleDoubleRight, faDownload } from '@fortawesome/free-solid-svg-icons';
import { getServiceTypes } from 'src/store/actions/service-type-actions';
import { getServiceLocations } from 'src/store/actions/service-location-actions';
import { IServiceType } from '../../types/service-types';
import { useDispatch } from 'react-redux';
import Multiselect from 'multiselect-react-dropdown';
import { IServiceLocation } from '../../types/service-locations';

type ColumnAdd = {
    headerClassName?: string;
    noDotsRow?: boolean;
};

export type Column<T extends Record<string, unknown>> = ColumnType<T> &
    UseSortByColumnOptions<T> &
    ColumnAdd &
    UseFiltersColumnOptions<T>;

interface Props<T extends Record<string, unknown>> {
    columns: Column<T>[];
    columnOpen?: boolean;
    setColumnOpen?: (open: boolean) => void;
    noLoader?: boolean;
    filtersInit?: any;
    filterGlobal?: boolean;
    calculateRatio?: boolean;
    data: Paginated<T, PaginatedBaseMeta>;
    getData: (params: QueryParams) => void;
    getDataCsv?: (params: QueryParams) => void;
    getDataInFile?: (params: QueryParams) => void;
    noDots?: boolean;
    noPagination?: boolean;
    classNameGrid?: string;
    inProgressCSV?: boolean;
    serviceTypes?: any;
    serviceLocations?: any;
    calc?: boolean;
    setCalc?: any;
}

const defaultColumn = {
    Filter: textFilter(),
};
function GlobalFilter({ value, setValue, serviceTypes, serviceLocations, calculateRatio, calc, setCalc }: any) {
    const onChange = useAsyncDebounce((change) => {
        setValue({ ...value, [change.id]: change.value });
    }, 200);

    const onCalculate = useAsyncDebounce(() => {
        setValue({ ...value, ['calculateRatio']: 1 });
        setCalc(true);
    }, 200);

    const onResetCalculate = useAsyncDebounce(() => {
        setValue({ ...value, ['calculateRatio']: 0 });
        setCalc(false);
    }, 200);

    const onSelect = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceType', value: selectedList });
    };

    const onRemove = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceType', value: selectedList });
    };

    const onSelectProvider = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceTypeProvider', value: selectedList });
    };

    const onRemoveProvider = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceTypeProvider', value: selectedList });
    };

    const onSelectLocation = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceLocation', value: selectedList });
    };

    const onRemoveLocation = (selectedList: any, selectedItem: any) => {
        onChange({ id: 'serviceLocation', value: selectedList });
    };

    const dispatch = useDispatch();

    useEffect(() => {
        if (!serviceLocations?.list?.length) {
            serviceLocations = dispatch(
                getServiceLocations({
                    page: 1,
                    itemsPerPage: 1200,
                }),
            );
        }
    }, []);
    const serviceLocationsValues: any = [];

    serviceLocations?.list?.forEach((stype: Partial<IServiceLocation>) => {
        const labelArr = [];
        if (stype.title) {
            labelArr.push(stype.title);
        }
        const label = labelArr.join(', ');
        serviceLocationsValues.push({ value: stype.id, label });
    });

    useEffect(() => {
        if (!serviceTypes?.list?.length) {
            serviceTypes = dispatch(
                getServiceTypes({
                    page: 1,
                    itemsPerPage: 1200,
                }),
            );
        }
    }, []);

    const serviceTypesValues: any = [];

    serviceTypes?.list?.forEach((stype: Partial<IServiceType>) => {
        const labelArr = [];
        if (stype.title) {
            labelArr.push(stype.title);
        }
        if (stype.cpt) {
            labelArr.push(`CPT: [${stype.cpt}]`);
        }
        const label = labelArr.join(', ');
        serviceTypesValues.push({ value: stype.id, cpt: `${stype.cpt ? stype.cpt : ''}`, label });
    });

    const style = {
        chips: {
            background: '#3e81b9',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
        },
        searchWrapper: {
            padding: '2px',
        },
        searchBox: {
            cursor: 'pointer',
            padding: '3px',
        },
        multiselectContainer: {
            color: '#3e81b9',
            zIndex: '15',
        },
        optionContainer: {
            zIndex: '15',
        },
        option: {
            background: 'white',
            color: '#3e81b9',
        },
    };

    const caseEnum = CaseEnum ? CaseEnum.getEnum('caseType') : {};
    const caseTypes: SelectObject[] = getValFromEnum(caseEnum);

    const bonusTypeEnum = GeneralEnum ? GeneralEnum.getEnum('BonusType') : {};
    const bonusTypes: SelectObject[] = getValFromEnum(bonusTypeEnum);

    const bonusStatusEnum = GeneralEnum ? GeneralEnum.getEnum('BonusStatus') : {};
    const bonusStatuses: SelectObject[] = getValFromEnum(bonusStatusEnum);

    const serviceTypeProviderEnum = OrderEnum ? OrderEnum.getEnum('ServiceTypeProvider') : {};
    const serviceTypeProviders: SelectObject[] = getValFromEnum(serviceTypeProviderEnum);

    return (
        <div>
            <div className={classNames(styles.firstFilterBlock)}>
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')}>
                    <span>From:</span>
                    <DateTimePicker
                        value={value && value.dateFrom ? moment(value.dateFrom).toDate() : undefined}
                        onChange={(date: any) => {
                            onChange({ id: 'dateFrom', value: date ? moment(date).format('YYYY-MM-DD') : undefined });
                        }}
                        time={false}
                        date={true}
                        format={'MMM D, YYYY'}
                    />
                </div>
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')}>
                    <span>To: </span>
                    <DateTimePicker
                        value={value && value.dateTo ? moment(value.dateTo).toDate() : undefined}
                        onChange={(date: any) => {
                            onChange({ id: 'dateTo', value: date ? moment(date).format('YYYY-MM-DD') : undefined });
                        }}
                        time={false}
                        date={true}
                        format={'MMM D, YYYY'}
                    />
                </div>
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                    <span>Case Type: </span>
                    <DropdownList
                        data={[{ label: 'All', value: '' }, ...caseTypes]}
                        containerClassName={'w-100'}
                        textField="label"
                        valueField="value"
                        onChange={(item: SelectObject) => {
                            onChange({ id: 'caseType', value: item.value ? item.value : undefined });
                        }}
                        value={value && value.caseType ? { value: value.caseType, label: '' } : undefined}
                    />
                </div>
                <br />
                <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                    <span>Service Type Provider: </span>
                    <Multiselect
                        options={[...serviceTypeProviders]} // Options to display in the dropdown
                        selectedValues={value && value.serviceTypeProvider ? value.serviceTypeProvider : undefined} // Preselected value to persist in dropdown
                        onSelect={onSelectProvider} // Function will trigger on select event
                        onRemove={onRemoveProvider} // Function will trigger on remove event
                        displayValue="label" // Property name to display in the dropdown options
                        closeIcon="close"
                        placeholder=""
                        style={style}
                        emptyRecordMsg=""
                        hidePlaceholder={true}
                        showArrow={true}
                    />
                </div>
                {serviceTypesValues ? (
                    <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                        <span>Service Type: </span>
                        <Multiselect
                            options={[...serviceTypesValues]} // Options to display in the dropdown
                            selectedValues={value && value.serviceType ? value.serviceType : undefined} // Preselected value to persist in dropdown
                            onSelect={onSelect} // Function will trigger on select event
                            onRemove={onRemove} // Function will trigger on remove event
                            displayValue="label" // Property name to display in the dropdown options
                            closeIcon="close"
                            placeholder=""
                            style={style}
                            emptyRecordMsg=""
                            hidePlaceholder={true}
                            showArrow={true}
                        />
                    </div>
                ) : (
                    <div></div>
                )}
                {serviceLocationsValues ? (
                    <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                        <span>Service Location: </span>
                        <Multiselect
                            options={[...serviceLocationsValues]}
                            selectedValues={value && value.serviceLocation ? value.serviceLocation : undefined}
                            onSelect={onSelectLocation}
                            onRemove={onRemoveLocation}
                            displayValue="label"
                            closeIcon="close"
                            placeholder=""
                            style={style}
                            emptyRecordMsg=""
                            hidePlaceholder={true}
                            showArrow={true}
                        />
                    </div>
                ) : (
                    <div></div>
                )}
                {!calculateRatio ? (
                    <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                        <span>Bonus Type: </span>
                        <DropdownList
                            data={[{ label: 'All', value: '' }, ...bonusTypes]}
                            containerClassName={'w-100'}
                            textField="label"
                            valueField="value"
                            onChange={(item: SelectObject) => {
                                onChange({ id: 'bonusType', value: item.value ? item.value : undefined });
                            }}
                            value={value && value.bonusType ? { value: value.bonusType, label: '' } : undefined}
                        />
                    </div>
                ) : null}
                {!calculateRatio ? (
                    <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                        <span>Bonus Status: </span>
                        <DropdownList
                            data={[{ label: 'All', value: '' }, ...bonusStatuses]}
                            containerClassName={'w-100'}
                            textField="label"
                            valueField="value"
                            onChange={(item: SelectObject) => {
                                onChange({ id: 'bonusStatus', value: item.value ? item.value : undefined });
                            }}
                            value={value && value.bonusStatus ? { value: value.bonusStatus, label: '' } : undefined}
                        />
                        <br />
                    </div>
                ) : null}
                {calculateRatio ? (
                    calc ? (
                        <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                            <span>
                                <i style={{ fontSize: '12px' }}>* Set a provider, student and supervisor before</i>
                            </span>
                            <div>
                                <button
                                    className={classNames(styles.btnCalculate, 'btn btn-info')}
                                    onClick={onResetCalculate}
                                >
                                    Reset calculation
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')} style={{ minWidth: 150 }}>
                            <span>
                                <i style={{ fontSize: '12px' }}>* Set a provider, student and supervisor before</i>
                            </span>
                            <div>
                                <button
                                    className={classNames(styles.btnCalculate, 'btn btn-info')}
                                    onClick={onCalculate}
                                >
                                    Calculate supervised ratio
                                </button>
                            </div>
                        </div>
                    )
                ) : null}
            </div>
        </div>
    );
}

function Grid<T extends Record<string, unknown>>(props: Props<T>): ReactElement {
    const [globalFilters, setGlobalFilters] = React.useState({
        dateFrom:
            props.filtersInit?.find((item: { id: string; value: string }) => item?.id === 'dateFrom')?.value ??
            moment().startOf('month').format('YYYY-MM-DD'),
        dateTo:
            props.filtersInit?.find((item: { id: string; value: string }) => item?.id === 'dateTo')?.value ??
            moment().endOf('month').format('YYYY-MM-DD'),
        serviceType: [],
        serviceTypeValues: [],
        serviceTypeProvider: [],
        serviceTypeProviderValues: [],
        serviceLocation: [],
        serviceLocationValues: [],
    });
    const {
        columns,
        data,
        getData,
        columnOpen,
        setColumnOpen,
        filtersInit = [],
        getDataCsv,
        getDataInFile,
        inProgressCSV,
        noLoader,
        noDots,
        noPagination,
        classNameGrid,
        serviceTypes,
        serviceLocations,
        calculateRatio,
        calc,
        setCalc,
    } = props;
    const match: any = useRouteMatch();
    // const filtersInit: any = [];

    useEffect(() => {
        if ((Object.values(match.params).length && match.params.status) || match.params.status) {
            Object.keys(match.params).forEach((k) => {
                filtersInit.push({ id: k, value: +match.params[k] });
            });
        }
    }, []); // eslint-disable-line

    const instance = useTable<T>(
        {
            columns,

            defaultColumn,
            data: data.list,
            manualSortBy: true,
            filterTypes: true,
            manualFilters: true,
            manualPagination: true,
            pageCount: Math.max(1, data.totalPages ?? 1),
            initialState: {
                pageIndex: data.page - 1,
                pageSize: 20,
                filters: filtersInit,
            },
        } as TableOptions<T>,
        useFilters,

        useSortBy,
        usePagination,
    );
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = instance;

    const { sortBy, filters, pageIndex, pageSize } = state as any;
    interface FiltersForCalc {
        provider: string | null;
        student: string | null;
        supervisor: string | null;
    }

    const [filtersForCalc, setFiltersForCalc] = useState<FiltersForCalc>({
        provider: null,
        student: null,
        supervisor: null,
    });

    const getPreparedFilters = useCallback(() => {
        const result: any = {};
        let filterProvider = null;
        let filterSupervisor = null;
        let filterStudent = null;
        filters.forEach((filter: { id: string; value: any }) => {
            result[filter.id] = filter.value;
            if (filter.id == 'providerId') {
                filterProvider = filter.value;
            } else if (filter.id == 'supervisor') {
                filterSupervisor = filter.value;
            } else if (filter.id == 'studentId') {
                filterStudent = filter.value;
            }
        });
        setFiltersForCalc({ provider: filterProvider, student: filterStudent, supervisor: filterSupervisor });

        return result;
    }, [filters]);
    const getPreparedSorting = useCallback(() => {
        const result: any = {};
        sortBy.forEach((sort: { id: string; desc: boolean }) => {
            result[sort.id] = sort.desc ? 'desc' : 'asc';
        });

        return result;
    }, [sortBy]);
    const updatedAt = useMemoCompare(Date.now(), () => {
        return data.meta?.updatedAt !== 0;
    });

    useEffect(() => {
        const serviceTypeFilters: any = [];
        globalFilters?.serviceType?.map((item: any, i: number) => {
            serviceTypeFilters.push(item.value);
        });
        const serviceTypeProviderFilters: any = [];
        globalFilters?.serviceTypeProvider?.map((item: any, i: number) => {
            serviceTypeProviderFilters.push(item.value);
        });
        const serviceLocationFilters: any = [];
        globalFilters?.serviceLocation?.map((item: any, i: number) => {
            serviceLocationFilters.push(item.value);
        });

        globalFilters.serviceTypeValues = serviceTypeFilters;
        globalFilters.serviceTypeProviderValues = serviceTypeProviderFilters;
        globalFilters.serviceLocationValues = serviceLocationFilters;

        const allSelectedFilters: any = { ...getPreparedFilters(), ...globalFilters };
        if (allSelectedFilters['serviceTypeProvider.id']) {
            allSelectedFilters.serviceTypeProvider = allSelectedFilters['serviceTypeProvider.id'];
        }

        const pf = getPreparedFilters();
        // globalFilters holds these as arrays of options (sent separately as *Values), so they must not
        // reach the API as-is; a column filter puts a plain scalar under the same key and has to survive.
        ['serviceType', 'serviceTypeProvider', 'serviceLocation'].forEach((key) => {
            const columnValue = pf ? pf[key] : undefined;
            if (columnValue === undefined || columnValue === null || columnValue === '' ||
                typeof columnValue === 'object') {
                delete allSelectedFilters[key];
            } else {
                allSelectedFilters[key] = columnValue;
            }
        });

        getData({
            page: pageIndex + 1,
            itemsPerPage: pageSize,
            filters: allSelectedFilters,
            sorting: getPreparedSorting(),
        });
    }, [getData, sortBy, filters, pageIndex, pageSize, updatedAt, globalFilters]); // eslint-disable-line

    const isProvider = user.getRoles().includes(UserRole.ROLE_USER);
    const isSupervisor = user.getSupervisor();

    const [providerHrs, setProviderHrs] = useState<number>(0);
    const [supervisorHrs, setSupervisorHrs] = useState<number>(0);
    const [supervisedRatio, setSupervisedRatio] = useState<number>(0);

    useEffect(() => {
        if (calc) {
            let prHrs = 0;
            let svHrs = 0;
            if (filtersForCalc && filtersForCalc.provider && filtersForCalc.supervisor && filtersForCalc.student) {
                data.list.forEach((it: any) => {
                    if (filtersForCalc.provider == it?.providerId) {
                        prHrs = prHrs + Number(it?.confirmedHours);
                    } else {
                        svHrs = svHrs + Number(it?.confirmedHours);
                    }
                });
                prHrs = Math.round(prHrs * 100) / 100;
                svHrs = Math.round(svHrs * 100) / 100;
            }
            setProviderHrs(prHrs);
            setSupervisorHrs(svHrs);

            setSupervisedRatio(
                !prHrs || !svHrs ? 0 : Math.round((Number((supervisorHrs / providerHrs) * 100) * 100) / 100),
            );
        }
    }, [data, supervisorHrs, providerHrs, supervisedRatio]);

    return (
        <div className={'position-relative'}>
            {getDataCsv ? (
                isProvider && !isSupervisor ? null : (
                    <button
                        className={classNames(styles.btnHeader, 'btn btn-outline-info')}
                        onClick={() => {
                            getDataCsv({
                                page: pageIndex + 1,
                                // itemsPerPage: pageSize,
                                filters: { ...getPreparedFilters(), ...globalFilters },
                                sorting: getPreparedSorting(),
                            });
                        }}
                    >
                        <FontAwesomeIcon className={'mr-1'} icon={faDownload} />
                        {inProgressCSV ? 'loading...' : 'Export .csv'}{' '}
                    </button>
                )
            ) : null}
            {getDataInFile ? (
                isProvider && !isSupervisor ? null : (
                    <button
                        className={classNames(styles.btnHeaderDownload, 'btn btn-outline-info')}
                        onClick={() => {
                            getDataInFile({
                                page: pageIndex + 1,
                                // itemsPerPage: pageSize,
                                filters: { ...getPreparedFilters(), ...globalFilters },
                                sorting: getPreparedSorting(),
                            });
                        }}
                    >
                        <FontAwesomeIcon icon={faDownload} /> {inProgressCSV ? 'loading...' : 'Details'}{' '}
                    </button>
                )
            ) : null}
            {props.filterGlobal && (
                <GlobalFilter
                    value={globalFilters}
                    setValue={setGlobalFilters}
                    serviceTypes={serviceTypes}
                    serviceLocations={serviceLocations}
                    calculateRatio={calculateRatio}
                    calc={calc}
                    setCalc={setCalc}
                />
            )}
            {calc ? (
                <div>
                    <i>
                        <b>
                            Provider, hrs: {providerHrs} <br /> Supervisor, hrs: {supervisorHrs} <br /> Supervised
                            ratio: {!isNaN(supervisedRatio) ? supervisedRatio : 0}%
                        </b>
                    </i>
                </div>
            ) : null}
            <div
                style={noDots ? {} : { minHeight: '450px' }}
                className={classNames(styles.table, 'table-responsive', classNameGrid)}
            >
                {data.inProgress && !noLoader ? <Loader /> : null}
                <table className={classNames('table')} {...getTableProps()}>
                    <thead>
                        {headerGroups.map((headerGroup, ind) => (
                            <Fragment key={ind}>
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column: any, i: number) => {
                                        if (i === headerGroup.headers.length - 1 && setColumnOpen) {
                                            return (
                                                <th
                                                    style={{ position: 'relative' }}
                                                    key={i}
                                                    {...column.getHeaderProps()}
                                                    width={column.width}
                                                >
                                                    <span style={{ position: 'absolute', bottom: '5px' }}>
                                                        {column.render('Header')}
                                                    </span>
                                                    <button
                                                        style={{
                                                            float: 'right',
                                                        }}
                                                        className={'btn btn-info ml-3'}
                                                        onClick={() => {
                                                            setColumnOpen(!columnOpen);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={columnOpen ? faAngleDoubleLeft : faAngleDoubleRight}
                                                        />
                                                    </button>
                                                </th>
                                            );
                                        }
                                        return (
                                            <th key={i} {...column.getHeaderProps()} width={column.width}>
                                                {column.canSort ? (
                                                    <span
                                                        className={column.headerClassName}
                                                        {...column.getSortByToggleProps()}
                                                    >
                                                        {column.render('Header')}
                                                        {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                                    </span>
                                                ) : (
                                                    <span>{column.render('Header')}</span>
                                                )}
                                            </th>
                                        );
                                    })}
                                </tr>
                                <tr>
                                    {headerGroup.headers.map((column: any, i: number) => (
                                        <th key={i} {...{ width: column.width }}>
                                            {column.canFilter ? column.render('Filter') : null}
                                        </th>
                                    ))}
                                </tr>
                            </Fragment>
                        ))}
                    </thead>
                    {noDots ? (
                        <tbody {...getTableBodyProps()}>
                            {data.list.length > 0 ? (
                                rows.map((row, i) => {
                                    if (row.index === 0 && row.original.totalRow) {
                                        prepareRow(row);
                                        return (
                                            <tr key={i} {...row.getRowProps()} className={classNames(styles.totalRow)}>
                                                {row.cells.map((cell, j: number) => {
                                                    return (
                                                        <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    }
                                    prepareRow(row);
                                    return (
                                        <tr
                                            key={i}
                                            {...row.getRowProps()}
                                            className={i % 2 < 1 ? classNames(styles.trOdd) : ''}
                                        >
                                            {row.cells.map((cell, j: number) => {
                                                return (
                                                    <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                        {cell.render('Cell')}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    {!data.inProgress && (
                                        <td colSpan={100} className={classNames(styles.empty, 'text-center', 'py-4')}>
                                            No results
                                        </td>
                                    )}
                                </tr>
                            )}
                        </tbody>
                    ) : (
                        <tbody {...getTableBodyProps()}>
                            {data.list.length > 0 ? (
                                rows.map((row, i) => {
                                    if (row.index === 0 && row.original.totalRow) {
                                        prepareRow(row);
                                        return (
                                            <tr key={i} {...row.getRowProps()} className={classNames(styles.totalRow)}>
                                                {row.cells.map((cell, j: number) => {
                                                    return (
                                                        <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    }
                                    prepareRow(row);
                                    return (
                                        <tr
                                            key={i}
                                            {...row.getRowProps()}
                                            className={i % 2 < 1 ? classNames(styles.trOdd) : ''}
                                        >
                                            {row.cells.map((cell: any, j: number) => {
                                                //:Cell<T,any>

                                                const noDots: any = cell.column.noDotsRow;
                                                return (
                                                    <td key={`${j}-Cell`} {...cell.getCellProps()}>
                                                        {noDots ? (
                                                            cell.render('Cell')
                                                        ) : (
                                                            <Dotdotdot clamp={3}>{cell.render('Cell')}</Dotdotdot>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    {!data.inProgress && (
                                        <td colSpan={100} className={classNames(styles.empty, 'text-center', 'py-4')}>
                                            No results
                                        </td>
                                    )}
                                </tr>
                            )}
                        </tbody>
                    )}
                </table>
                {!noPagination ? <Pagination<T> table={instance} /> : null}
            </div>
        </div>
    );
}

export default Grid;
